import Controller from '@ember/controller';
import { action, set } from '@ember/object';
import { ajax } from 'discourse/lib/ajax';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';

function slugify(text) {
  return (text || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const BLANK_GUIDE = {
  slug: '',
  title: '',
  meta_title: '',
  meta_description: '',
  h1: '',
  content: '',
  category: 'getting-started',
  target_keyword: '',
  relatedProviderSlugsText: '',
  faqItems: [],
  published: false,
  canonical_url: '',
  authorUsername: '',
};

const BLANK_COMPARISON = {
  provider_a_slug: '',
  provider_b_slug: '',
  title: '',
  meta_description: '',
  summary: '',
  verdict: '',
  winner_slug: '',
  published: false,
};

const BLANK_BEST_FOR = {
  slug: '',
  title: '',
  meta_title: '',
  meta_description: '',
  published: false,
};

const BLANK_ALTERNATIVE = {
  provider_slug: '',
  meta_title: '',
  meta_description: '',
  custom_alternatives_slugs_text: '',
};

export default class AdminPluginsProxyviewsSeoController extends Controller {
  @service dialog;
  @tracked activeTab = 'guides';
  @tracked message = null;
  @tracked savingId = null;

  // ── Guides ───────────────────────────────────────────────────────────
  @tracked guides = [];
  @tracked guidesMeta = {};
  @tracked guidesPage = 1;
  @tracked guidesLoading = false;
  @tracked guidesSearch = '';
  @tracked guideCategoryFilter = '';
  @tracked categories = [];
  @tracked baseCategories = [];
  @tracked customCategories = [];
  @tracked newCategoryName = '';
  @tracked categoryError = null;
  @tracked guideFormMode = null; // null | 'new' | 'edit'
  @tracked guideForm = { ...BLANK_GUIDE };
  @tracked editingGuideId = null;
  @tracked guideStructuredDataPreview = null;
  @tracked guideStructuredDataLoading = false;
  @tracked guideAuthorUsernameTouched = false;
  @tracked guideSlugManuallyEdited = false;

  _searchDebounceTimer = null;
  _keydownHandler = null;

  // ── Comparisons ──────────────────────────────────────────────────────
  @tracked comparisons = [];
  @tracked comparisonsMeta = {};
  @tracked comparisonsPage = 1;
  @tracked comparisonsLoading = false;
  @tracked comparisonsSearch = '';
  @tracked comparisonFormMode = null;
  @tracked comparisonForm = { ...BLANK_COMPARISON };
  @tracked editingComparisonId = null;
  @tracked comparisonStructuredDataPreview = null;
  @tracked comparisonStructuredDataLoading = false;
  @tracked comparisonMatrixRows = [];

  // ── Best For ─────────────────────────────────────────────────────────
  @tracked bestForUseCases = [];
  @tracked bestForSearch = '';
  @tracked bestForFilterTab = 'all';
  @tracked bestForFormMode = null;
  @tracked bestForForm = { ...BLANK_BEST_FOR };
  @tracked editingBestForId = null;

  // ── Best For Provider Management ─────────────────────────────────────
  @tracked managingBestForSlug = null;
  @tracked managingBestForTitle = null;
  @tracked bestForProviders = [];
  @tracked bestForProviderSearch = '';
  @tracked bestForSearchResults = [];
  @tracked bestForDirtyOrder = false;

  // ── Alternatives ─────────────────────────────────────────────────────
  @tracked alternatives = [];
  @tracked alternativeFormMode = null;
  @tracked alternativeForm = { ...BLANK_ALTERNATIVE };
  @tracked editingAlternativeId = null;

  // ── Robots.txt ───────────────────────────────────────────────────────
  @tracked robotsEnabled = false;
  @tracked robotsCustomRules = '';
  @tracked robotsPreview = '';
  @tracked robotsUpdatedAt = null;
  @tracked robotsUpdatedBy = null;
  @tracked robotsSaving = false;

  // ── Sitemap ──────────────────────────────────────────────────────────
  @tracked sitemapEnabled = false;
  @tracked sitemapUrl = '';
  @tracked sitemapLastGeneratedAt = null;
  @tracked sitemapUrlCount = null;
  @tracked sitemapRegenerating = false;

  // ── Coverage inspector ───────────────────────────────────────────────
  @tracked coverageData = null;
  @tracked coverageLoading = false;

  // ── Robots.txt sandbox ───────────────────────────────────────────────
  @tracked draftRobotsRules = '';
  @tracked draftPreview = null;
  @tracked draftLoading = false;

  get guideWordCount() {
    return (this.guideForm.content || '').trim().split(/\s+/).filter(Boolean).length;
  }

  get guideWordCountBelowThreshold() {
    return this.guideWordCount < 300;
  }

  @action
  changeTab(tab) {
    this.activeTab = tab;
    this.message = null;
    if (tab === 'coverage' && !this.coverageData && !this.coverageLoading) {
      this.loadCoverage();
    }
  }

  @action
  dismissMessage() {
    this.message = null;
  }

  @action
  async toggleGuidePublished(guide) {
    const isCurrentlyPublished = Boolean(guide.published);
    const message = isCurrentlyPublished
      ? `Are you sure you want to unpublish "${guide.title}"? This will hide the guide from visitors and remove it from search engine indexes.`
      : `Are you sure you want to publish "${guide.title}"? This will make the guide publicly accessible and include it in the XML sitemap.`;

    this.dialog.yesNoConfirm({
      message,
      didConfirm: async () => {
        const newStatus = !isCurrentlyPublished;
        set(guide, 'published', newStatus);
        this.guides = this.guides.map((g) => {
          if (String(g.id) === String(guide.id)) {
            set(g, 'published', newStatus);
            return { ...g, published: newStatus };
          }
          return g;
        });
        try {
          const res = await ajax(`/admin/plugins/proxyviews-seo/guides/${guide.id}`, {
            type: 'PUT',
            data: { guide: { published: newStatus } },
          });
          if (res?.guide) {
            const finalStatus = Boolean(res.guide.published);
            set(guide, 'published', finalStatus);
            if (res.guide.published_at) set(guide, 'published_at', res.guide.published_at);
            this.guides = this.guides.map((g) => {
              if (String(g.id) === String(guide.id)) {
                set(g, 'published', finalStatus);
                if (res.guide.published_at) set(g, 'published_at', res.guide.published_at);
                return { ...g, published: finalStatus, published_at: res.guide.published_at };
              }
              return g;
            });
          }
          this.message = {
            text: newStatus ? `Published "${guide.title}".` : `Unpublished "${guide.title}".`,
            type: 'success',
          };
          this.clearMessageAfterDelay();
        } catch (err) {
          set(guide, 'published', isCurrentlyPublished);
          this.guides = this.guides.map((g) => {
            if (String(g.id) === String(guide.id)) {
              set(g, 'published', isCurrentlyPublished);
              return { ...g, published: isCurrentlyPublished };
            }
            return g;
          });
          const msg =
            err?.responseJSON?.errors?.[0] ||
            err?.responseJSON?.error ||
            'Failed to update publish status.';
          this.message = { text: msg, type: 'danger' };
          this.clearMessageAfterDelay();
        }
      },
    });
  }

  @action
  updateGuidesSearch(e) {
    this.guidesSearch = e.target.value;
    this.guidesPage = 1;
    if (this._searchDebounceTimer) clearTimeout(this._searchDebounceTimer);
    this._searchDebounceTimer = setTimeout(() => this.loadGuides(), 300);
  }

  @action
  filterByCategory(category) {
    this.guideCategoryFilter = this.guideCategoryFilter === category ? '' : category;
    this.guidesSearch = '';
    this.guidesPage = 1;
    this.loadGuides();
  }

  _setupKeydownListener() {
    if (this._keydownHandler) return;
    this._keydownHandler = (e) => {
      if (e.key === 'Escape') {
        if (this.guideFormMode) this.cancelGuideForm();
        else if (this.comparisonFormMode) this.cancelComparisonForm();
        else if (this.bestForFormMode) this.cancelBestForForm();
        else if (this.alternativeFormMode) this.cancelAlternativeForm();
      }
    };
    document.addEventListener('keydown', this._keydownHandler);
  }

  _teardownKeydownListener() {
    if (this._keydownHandler) {
      document.removeEventListener('keydown', this._keydownHandler);
      this._keydownHandler = null;
    }
  }

  async loadCoverage() {
    this.coverageLoading = true;
    try {
      const data = await ajax('/admin/plugins/proxyviews-seo/coverage');
      this.coverageData = data;
    } catch (err) {
      this.message = { text: 'Failed to load coverage report.', type: 'danger' };
    } finally {
      this.coverageLoading = false;
    }
  }

  async loadGuides() {
    this.guidesLoading = true;
    try {
      const data = await ajax('/admin/plugins/proxyviews-seo/guides', {
        data: { q: this.guidesSearch, page: this.guidesPage, category: this.guideCategoryFilter },
      });
      this.guides = data.guides || [];
      this.guidesMeta = data.meta || {};
      if (data.categories) this.categories = data.categories;
      if (data.base_categories) this.baseCategories = data.base_categories;
      if (data.custom_categories) this.customCategories = data.custom_categories;
    } catch (err) {
      this.message = { text: 'Failed to load guides.', type: 'danger' };
    } finally {
      this.guidesLoading = false;
    }
  }

  @action
  updateNewCategoryName(event) {
    this.newCategoryName = event.target.value;
  }

  @action
  async addCategory() {
    if (!this.newCategoryName.trim()) return;
    this.categoryError = null;
    try {
      const data = await ajax('/admin/plugins/proxyviews-seo/guide-categories', {
        type: 'POST',
        data: { name: this.newCategoryName },
      });
      this.categories = data.categories || this.categories;
      this.customCategories = data.custom_categories || this.customCategories;
      this.newCategoryName = '';
    } catch (err) {
      this.categoryError = err.jqXHR?.responseJSON?.errors?.join(', ') || 'Failed to add category.';
    }
  }

  @action
  removeCategory(category) {
    this.dialog.yesNoConfirm({
      message: `Remove category "${category}"? Guides currently using it must be recategorized first.`,
      didConfirm: async () => {
        this.categoryError = null;
        try {
          const data = await ajax(`/admin/plugins/proxyviews-seo/guide-categories/${category}`, {
            type: 'DELETE',
          });
          this.categories = data.categories || this.categories;
          this.customCategories = data.custom_categories || this.customCategories;
        } catch (err) {
          this.categoryError =
            err.jqXHR?.responseJSON?.errors?.join(', ') || 'Failed to remove category.';
        }
      },
    });
  }

  @action
  nextGuidesPage() {
    if (this.guidesPage >= (this.guidesMeta.total_pages || 1) || this.guidesLoading) {
      return;
    }
    this.guidesPage += 1;
    this.loadGuides();
    this.scrollToTable();
  }

  @action
  prevGuidesPage() {
    if (this.guidesPage <= 1 || this.guidesLoading) {
      return;
    }
    this.guidesPage -= 1;
    this.loadGuides();
    this.scrollToTable();
  }

  @action
  startNewGuide() {
    this.guideForm = { ...BLANK_GUIDE };
    this.editingGuideId = null;
    this.guideFormMode = 'new';
    this.guideStructuredDataPreview = null;
    this.guideSlugManuallyEdited = false;
    this.guideAuthorUsernameTouched = false;
    this._setupKeydownListener();
  }

  @action
  async startEditGuide(guide) {
    this.activeTab = 'guides';
    this.savingId = 'load-' + guide.id;
    try {
      const data = await ajax(`/admin/plugins/proxyviews-seo/guides/${guide.id}`);
      const g = data.guide;
      // FAQ can be stored as Array or Hash {"0" => {...}} — normalize to array
      const rawFaq = g.faq || [];
      const faqArray = Array.isArray(rawFaq)
        ? rawFaq
        : (rawFaq && typeof rawFaq === 'object' ? Object.values(rawFaq) : []);
      this.guideForm = {
        slug: g.slug,
        title: g.title,
        meta_title: g.meta_title || '',
        meta_description: g.meta_description || '',
        h1: g.h1 || '',
        content: g.content || '',
        category: g.category || 'getting-started',
        target_keyword: g.target_keyword || '',
        relatedProviderSlugsText: (g.related_provider_slugs || []).join(', '),
        faqItems: faqArray.map((item) => ({
          q: (item && (item.q || item['q'])) || '',
          a: (item && (item.a || item['a'])) || '',
        })),
        published: g.published,
        canonical_url: g.canonical_url || '',
        authorUsername: g.author_username || '',
      };
      this.editingGuideId = guide.id;
      this.guideFormMode = 'edit';
      this.guideStructuredDataPreview = null;
      this.guideAuthorUsernameTouched = false;
      this.guideSlugManuallyEdited = true;
      this._setupKeydownListener();
    } catch (err) {
      this.message = { text: 'Failed to load guide.', type: 'danger' };
    } finally {
      this.savingId = null;
    }
  }

  @action
  cancelGuideForm() {
    this.guideFormMode = null;
    this.editingGuideId = null;
    this.guideStructuredDataPreview = null;
    this.guideAuthorUsernameTouched = false;
    this._teardownKeydownListener();
  }

  @action
  stopModalClick(e) {
    e.stopPropagation();
  }

  @action
  updateGuideAuthorUsername(event) {
    this.guideAuthorUsernameTouched = true;
    this.guideForm = { ...this.guideForm, authorUsername: event.target.value };
  }

  @action
  async previewGuideStructuredData() {
    if (!this.editingGuideId) return;
    this.guideStructuredDataLoading = true;
    try {
      const data = await ajax(
        `/admin/plugins/proxyviews-seo/guides/${this.editingGuideId}/structured-data`,
      );
      this.guideStructuredDataPreview = JSON.stringify(data.structured_data, null, 2);
    } catch (err) {
      this.message = { text: 'Failed to load structured data preview.', type: 'danger' };
    } finally {
      this.guideStructuredDataLoading = false;
    }
  }

  @action
  updateGuideField(field, event) {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    this.guideForm = { ...this.guideForm, [field]: value };
  }

  @action
  updateGuideTitle(event) {
    const title = event.target.value;
    const nextSlug = this.guideSlugManuallyEdited ? this.guideForm.slug : slugify(title);
    this.guideForm = { ...this.guideForm, title, slug: nextSlug };
  }

  @action
  updateGuideSlug(event) {
    const slug = event.target.value;
    this.guideSlugManuallyEdited = slug.trim().length > 0;
    this.guideForm = { ...this.guideForm, slug };
  }

  @action
  updateGuideFieldValue(field, value) {
    // DEditor's @change passes a DOM event, not a string value
    const val = value?.target?.value !== undefined ? value.target.value : value;
    this.guideForm = { ...this.guideForm, [field]: val };
  }

  @action
  addFaqItem() {
    this.guideForm = {
      ...this.guideForm,
      faqItems: [...this.guideForm.faqItems, { q: '', a: '' }],
    };
  }

  @action
  removeFaqItem(index) {
    this.guideForm = {
      ...this.guideForm,
      faqItems: this.guideForm.faqItems.filter((_, i) => i !== index),
    };
  }

  @action
  updateFaqItemField(index, field, event) {
    const faqItems = this.guideForm.faqItems.map((item, i) =>
      i === index ? { ...item, [field]: event.target.value } : item,
    );
    this.guideForm = { ...this.guideForm, faqItems };
  }

  buildGuidePayload() {
    const faq = this.guideForm.faqItems
      .map((item) => ({ q: (item.q || '').trim(), a: (item.a || '').trim() }))
      .filter((item) => item.q && item.a);

    const payload = {
      slug: this.guideForm.slug,
      title: this.guideForm.title,
      meta_title: this.guideForm.meta_title,
      meta_description: this.guideForm.meta_description,
      h1: this.guideForm.h1,
      content: this.guideForm.content,
      category: this.guideForm.category,
      target_keyword: this.guideForm.target_keyword,
      related_provider_slugs: this.guideForm.relatedProviderSlugsText
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      faq,
      published: this.guideForm.published,
      canonical_url: this.guideForm.canonical_url,
    };

    if (this.guideAuthorUsernameTouched) {
      payload.author_username = this.guideForm.authorUsername.trim();
    }

    return payload;
  }

  @action
  async saveGuide() {
    this.savingId = 'save-guide';
    this.message = null;
    let payload;
    try {
      payload = this.buildGuidePayload();
    } catch (e) {
      this.message = { text: e.message, type: 'danger' };
      this.savingId = null;
      return;
    }

    try {
      if (this.editingGuideId) {
        await ajax(`/admin/plugins/proxyviews-seo/guides/${this.editingGuideId}`, {
          type: 'PUT',
          data: { guide: payload },
        });
        this.message = { text: `Saved "${payload.title}".`, type: 'success' };
      } else {
        await ajax('/admin/plugins/proxyviews-seo/guides', {
          type: 'POST',
          data: { guide: payload },
        });
        this.message = { text: `Created "${payload.title}".`, type: 'success' };
      }
      this.guideFormMode = null;
      this.editingGuideId = null;
      this.clearMessageAfterDelay();
      await this.loadGuides();
    } catch (err) {
      this.message = {
        text: err.responseJSON?.errors?.join(', ') || 'Failed to save guide.',
        type: 'danger',
      };
    } finally {
      this.savingId = null;
    }
  }

  @action
  async deleteGuide(guide) {
    this.dialog.yesNoConfirm({
      message: `Delete guide "${guide.title}"? This cannot be undone.`,
      didConfirm: async () => {
        this.savingId = 'delete-guide-' + guide.id;
        this.message = null;
        try {
          await ajax(`/admin/plugins/proxyviews-seo/guides/${guide.id}`, { type: 'DELETE' });
          this.guides = this.guides.filter((g) => g.id !== guide.id);
          this.message = { text: `Deleted "${guide.title}".`, type: 'success' };
          this.clearMessageAfterDelay();
        } catch (err) {
          this.message = { text: 'Failed to delete guide.', type: 'danger' };
        } finally {
          this.savingId = null;
        }
      },
    });
  }

  async loadComparisons() {
    this.comparisonsLoading = true;
    try {
      const data = await ajax('/admin/plugins/proxyviews-seo/comparisons', {
        data: { q: this.comparisonsSearch, page: this.comparisonsPage },
      });
      this.comparisons = data.comparisons || [];
      this.comparisonsMeta = data.meta || {};
    } catch (err) {
      this.message = { text: 'Failed to load comparisons.', type: 'danger' };
    } finally {
      this.comparisonsLoading = false;
    }
  }

  @action
  updateComparisonsSearch(e) {
    this.comparisonsSearch = e.target.value;
    this.comparisonsPage = 1;
    this.loadComparisons();
  }

  @action
  nextComparisonsPage() {
    if (
      this.comparisonsPage >= (this.comparisonsMeta.total_pages || 1) ||
      this.comparisonsLoading
    ) {
      return;
    }
    this.comparisonsPage += 1;
    this.loadComparisons();
    this.scrollToTable();
  }

  @action
  prevComparisonsPage() {
    if (this.comparisonsPage <= 1 || this.comparisonsLoading) {
      return;
    }
    this.comparisonsPage -= 1;
    this.loadComparisons();
    this.scrollToTable();
  }

  @action
  startNewComparison() {
    this.comparisonForm = { ...BLANK_COMPARISON };
    this.editingComparisonId = null;
    this.comparisonFormMode = 'new';
    this.comparisonStructuredDataPreview = null;
    this.comparisonMatrixRows = [];
    this._setupKeydownListener();
  }

  @action
  async startEditComparison(comparison) {
    this.activeTab = 'comparisons';
    this.savingId = 'load-' + comparison.id;
    try {
      const data = await ajax(`/admin/plugins/proxyviews-seo/comparisons/${comparison.id}`);
      const c = data.comparison;
      this.comparisonForm = {
        provider_a_slug: c.provider_a_slug,
        provider_b_slug: c.provider_b_slug,
        title: c.title,
        meta_description: c.meta_description || '',
        summary: c.summary || '',
        verdict: c.verdict || '',
        winner_slug: c.winner_slug || '',
        published: c.published,
      };
      const matrix = c.comparison_data || {};
      const slugA = c.provider_a_slug;
      const slugB = c.provider_b_slug;
      this.comparisonMatrixRows = Object.entries(matrix)
        .filter(([key]) => key !== 'faq' && key !== 'providers')
        .map(([key, vals]) => ({
          metric: key.replace(/[-_]/g, ' '),
          value_a: vals[slugA] ?? vals[slugA?.replace(/-/g, '_')] ?? '',
          value_b: vals[slugB] ?? vals[slugB?.replace(/-/g, '_')] ?? '',
        }));
      this.editingComparisonId = comparison.id;
      this.comparisonFormMode = 'edit';
      this.comparisonStructuredDataPreview = null;
      this._setupKeydownListener();
    } catch (err) {
      this.message = { text: 'Failed to load comparison.', type: 'danger' };
    } finally {
      this.savingId = null;
    }
  }

  @action
  cancelComparisonForm() {
    this.comparisonFormMode = null;
    this.editingComparisonId = null;
    this.comparisonStructuredDataPreview = null;
    this._teardownKeydownListener();
  }

  @action
  async previewComparisonStructuredData() {
    if (!this.editingComparisonId) return;
    this.comparisonStructuredDataLoading = true;
    try {
      const data = await ajax(
        `/admin/plugins/proxyviews-seo/comparisons/${this.editingComparisonId}/structured-data`,
      );
      this.comparisonStructuredDataPreview = JSON.stringify(data.structured_data, null, 2);
    } catch (err) {
      this.message = { text: 'Failed to load structured data preview.', type: 'danger' };
    } finally {
      this.comparisonStructuredDataLoading = false;
    }
  }

  @action
  updateComparisonField(field, event) {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    this.comparisonForm = { ...this.comparisonForm, [field]: value };
  }

  @action
  updateComparisonFieldValue(field, value) {
    this.comparisonForm = { ...this.comparisonForm, [field]: value };
  }

  @action
  addMatrixRow() {
    this.comparisonMatrixRows = [
      ...this.comparisonMatrixRows,
      { metric: '', value_a: '', value_b: '' },
    ];
  }

  @action
  removeMatrixRow(index) {
    this.comparisonMatrixRows = this.comparisonMatrixRows.filter((_, i) => i !== index);
  }

  @action
  updateMatrixRow(index, field, event) {
    this.comparisonMatrixRows = this.comparisonMatrixRows.map((row, i) =>
      i === index ? { ...row, [field]: event.target.value } : row,
    );
  }

  @action
  async toggleComparisonPublished(comparison) {
    const isCurrentlyPublished = Boolean(comparison.published);
    const message = isCurrentlyPublished
      ? `Are you sure you want to unpublish "${comparison.title}"? This comparison page will no longer be visible to site visitors.`
      : `Are you sure you want to publish "${comparison.title}"? This comparison page will become publicly visible.`;

    this.dialog.yesNoConfirm({
      message,
      didConfirm: async () => {
        const newStatus = !isCurrentlyPublished;
        set(comparison, 'published', newStatus);
        this.comparisons = this.comparisons.map((c) => {
          if (String(c.id) === String(comparison.id)) {
            set(c, 'published', newStatus);
            return { ...c, published: newStatus };
          }
          return c;
        });
        try {
          const res = await ajax(`/admin/plugins/proxyviews-seo/comparisons/${comparison.id}`, {
            type: 'PUT',
            data: { comparison: { published: newStatus } },
          });
          if (res?.comparison) {
            const finalStatus = Boolean(res.comparison.published);
            set(comparison, 'published', finalStatus);
            this.comparisons = this.comparisons.map((c) => {
              if (String(c.id) === String(comparison.id)) {
                set(c, 'published', finalStatus);
                return { ...c, published: finalStatus };
              }
              return c;
            });
          }
          this.message = {
            text: newStatus
              ? `Published "${comparison.title}".`
              : `Unpublished "${comparison.title}".`,
            type: 'success',
          };
          this.clearMessageAfterDelay();
        } catch (err) {
          set(comparison, 'published', isCurrentlyPublished);
          this.comparisons = this.comparisons.map((c) => {
            if (String(c.id) === String(comparison.id)) {
              set(c, 'published', isCurrentlyPublished);
              return { ...c, published: isCurrentlyPublished };
            }
            return c;
          });
          this.message = {
            text: err?.responseJSON?.errors?.[0] || 'Failed to update publish status.',
            type: 'danger',
          };
          this.clearMessageAfterDelay();
        }
      },
    });
  }

  @action
  async toggleBestForPublished(item) {
    const isCurrentlyPublished = Boolean(item.published);
    const message = isCurrentlyPublished
      ? `Are you sure you want to unpublish "${item.title}"? This use case landing page will be hidden from public view.`
      : `Are you sure you want to publish "${item.title}"? This use case landing page will become publicly visible.`;

    this.dialog.yesNoConfirm({
      message,
      didConfirm: async () => {
        const newStatus = !isCurrentlyPublished;
        set(item, 'published', newStatus);
        this.bestForUseCases = this.bestForUseCases.map((uc) => {
          if (String(uc.id) === String(item.id)) {
            set(uc, 'published', newStatus);
            return { ...uc, published: newStatus };
          }
          return uc;
        });
        try {
          const res = await ajax(`/admin/plugins/proxyviews-seo/best-for/${item.id}`, {
            type: 'PUT',
            data: { best_for_use_case: { published: newStatus } },
          });
          if (res?.best_for_use_case) {
            const finalStatus = Boolean(res.best_for_use_case.published);
            set(item, 'published', finalStatus);
            this.bestForUseCases = this.bestForUseCases.map((uc) => {
              if (String(uc.id) === String(item.id)) {
                set(uc, 'published', finalStatus);
                return { ...uc, published: finalStatus };
              }
              return uc;
            });
          }
          this.message = {
            text: newStatus ? `Published "${item.title}".` : `Unpublished "${item.title}".`,
            type: 'success',
          };
          this.clearMessageAfterDelay();
        } catch (err) {
          set(item, 'published', isCurrentlyPublished);
          this.bestForUseCases = this.bestForUseCases.map((uc) => {
            if (String(uc.id) === String(item.id)) {
              set(uc, 'published', isCurrentlyPublished);
              return { ...uc, published: isCurrentlyPublished };
            }
            return uc;
          });
          this.message = {
            text: err?.responseJSON?.errors?.[0] || 'Failed to update publish status.',
            type: 'danger',
          };
          this.clearMessageAfterDelay();
        }
      },
    });
  }

  @action
  async saveComparison() {
    this.savingId = 'save-comparison';
    this.message = null;
    const payload = { ...this.comparisonForm };

    const slugA = payload.provider_a_slug;
    const slugB = payload.provider_b_slug;
    const comparisonData = {};
    this.comparisonMatrixRows.forEach((row, i) => {
      if (!row.metric?.trim()) return;
      const key = row.metric.trim().toLowerCase().replace(/\s+/g, '_');
      comparisonData[key] = {};
      comparisonData[key][slugA] = row.value_a ?? '';
      comparisonData[key][slugB] = row.value_b ?? '';
    });
    if (Object.keys(comparisonData).length > 0) {
      payload.comparison_data = JSON.stringify(comparisonData);
    }

    try {
      if (this.editingComparisonId) {
        const { provider_a_slug, provider_b_slug, ...updatable } = payload;
        await ajax(`/admin/plugins/proxyviews-seo/comparisons/${this.editingComparisonId}`, {
          type: 'PUT',
          data: { comparison: updatable },
        });
        this.message = { text: `Saved "${payload.title}".`, type: 'success' };
      } else {
        await ajax('/admin/plugins/proxyviews-seo/comparisons', {
          type: 'POST',
          data: { comparison: payload },
        });
        this.message = { text: `Created comparison.`, type: 'success' };
      }
      this.comparisonFormMode = null;
      this.editingComparisonId = null;
      this.clearMessageAfterDelay();
      await this.loadComparisons();
    } catch (err) {
      this.message = {
        text: err.responseJSON?.errors?.join(', ') || 'Failed to save comparison.',
        type: 'danger',
      };
    } finally {
      this.savingId = null;
    }
  }

  @action
  async deleteComparison(comparison) {
    this.dialog.yesNoConfirm({
      message: `Delete comparison "${comparison.title}"? This cannot be undone.`,
      didConfirm: async () => {
        this.savingId = 'delete-comparison-' + comparison.id;
        this.message = null;
        try {
          await ajax(`/admin/plugins/proxyviews-seo/comparisons/${comparison.id}`, {
            type: 'DELETE',
          });
          this.comparisons = this.comparisons.filter((c) => c.id !== comparison.id);
          this.message = { text: `Deleted "${comparison.title}".`, type: 'success' };
          this.clearMessageAfterDelay();
        } catch (err) {
          this.message = { text: 'Failed to delete comparison.', type: 'danger' };
        } finally {
          this.savingId = null;
        }
      },
    });
  }

  clearMessageAfterDelay() {
    setTimeout(() => {
      this.message = null;
    }, 4000);
  }

  async loadBestFor() {
    try {
      const data = await ajax('/admin/plugins/proxyviews-seo/best-for');
      this.bestForUseCases = data.use_cases || [];
    } catch (err) {
      this.message = { text: 'Failed to load best for use cases.', type: 'danger' };
    }
  }

  BEST_FOR_PRESETS = [
    {
      label: '🕸️ Web Scraping',
      slug: 'web-scraping',
      title: 'Best Proxies for Web Scraping',
      meta_title: 'Best Proxies for Web Scraping (2026 Ratings) | ProxyViews',
      meta_description:
        'Discover the top-rated residential and datacenter proxy providers for large-scale web scraping and data extraction.',
    },
    {
      label: '👟 Sneaker Bots',
      slug: 'sneaker-bots',
      title: 'Best Proxies for Sneaker Bots',
      meta_title: 'Best Proxies for Sneaker Bots & Copping | ProxyViews',
      meta_description:
        'Fast, low-latency residential and ISP proxies tailored for Nike, Supreme, Shopify, and Footsites.',
    },
    {
      label: '📊 SEO & SERP',
      slug: 'seo-monitoring',
      title: 'Best Proxies for SEO Monitoring',
      meta_title: 'Best Proxies for SEO Monitoring & Rank Tracking | ProxyViews',
      meta_description:
        'Geo-targeted proxy networks for rank tracking, SERP data collection, and competitor research.',
    },
    {
      label: '📱 Social Media',
      slug: 'social-media',
      title: 'Best Proxies for Social Media',
      meta_title: 'Best Proxies for Social Media Automation | ProxyViews',
      meta_description:
        'Clean residential IP pools for managing Instagram, TikTok, LinkedIn, and Twitter accounts.',
    },
    {
      label: '🛡️ Ad Verification',
      slug: 'ad-verification',
      title: 'Best Proxies for Ad Verification',
      meta_title: 'Best Proxies for Ad Verification & Fraud Detection | ProxyViews',
      meta_description:
        'Verify digital ad placements, affiliate links, and landing pages globally with residential IPs.',
    },
    {
      label: '🤖 AI Data Collection',
      slug: 'ai-data-collection',
      title: 'Best Proxies for AI Data Scraping',
      meta_title: 'Best Proxies for AI Model Training & Data Scraping | ProxyViews',
      meta_description:
        'High-concurrency proxy solutions for gathering AI training datasets at scale.',
    },
    {
      label: '🛍️ E-Commerce',
      slug: 'ecommerce',
      title: 'Best Proxies for E-Commerce Scraping',
      meta_title: 'Best Proxies for E-Commerce & Price Monitoring | ProxyViews',
      meta_description:
        'Real-time price intelligence and stock tracking proxies for Amazon, eBay, and retail sites.',
    },
  ];

  @action
  setBestForFilterTab(tab) {
    this.bestForFilterTab = tab;
  }

  @action
  applyBestForPreset(preset) {
    this.bestForForm = {
      ...this.bestForForm,
      slug: preset.slug,
      title: preset.title,
      meta_title: preset.meta_title,
      meta_description: preset.meta_description,
    };
  }

  @action
  updateBestForSearch(e) {
    this.bestForSearch = e.target.value;
  }

  get filteredBestForUseCases() {
    let list = this.bestForUseCases || [];

    if (this.bestForFilterTab === 'published') {
      list = list.filter((uc) => uc.published);
    } else if (this.bestForFilterTab === 'drafts') {
      list = list.filter((uc) => !uc.published);
    } else if (this.bestForFilterTab === 'missing-meta') {
      list = list.filter((uc) => !uc.meta_title || !uc.meta_description);
    }

    if (!this.bestForSearch) {
      return list;
    }
    const q = this.bestForSearch.toLowerCase().trim();
    return list.filter(
      (uc) =>
        (uc.title && uc.title.toLowerCase().includes(q)) ||
        (uc.slug && uc.slug.toLowerCase().includes(q)),
    );
  }

  get bestForCounts() {
    const all = (this.bestForUseCases || []).length;
    const published = (this.bestForUseCases || []).filter((uc) => uc.published).length;
    const drafts = all - published;
    const missingMeta = (this.bestForUseCases || []).filter(
      (uc) => !uc.meta_title || !uc.meta_description,
    ).length;
    return { all, published, drafts, missingMeta };
  }

  get bestForMetaTitleCount() {
    return (this.bestForForm.meta_title || '').length;
  }

  get bestForMetaDescriptionCount() {
    return (this.bestForForm.meta_description || '').length;
  }

  @action
  startNewBestFor() {
    this.bestForForm = { ...BLANK_BEST_FOR };
    this.editingBestForId = null;
    this.bestForFormMode = 'new';
    this._setupKeydownListener();
  }

  @action
  async startEditBestFor(useCase) {
    this.savingId = 'load-best-for-' + useCase.id;
    try {
      const data = await ajax(`/admin/plugins/proxyviews-seo/best-for/${useCase.id}`);
      const uc = data.use_case;
      this.bestForForm = {
        slug: uc.slug,
        title: uc.title || '',
        meta_title: uc.meta_title || '',
        meta_description: uc.meta_description || '',
        published: uc.published,
      };
      this.editingBestForId = useCase.id;
      this.bestForFormMode = 'edit';
      this._setupKeydownListener();
    } catch (err) {
      this.message = { text: 'Failed to load use case.', type: 'danger' };
    } finally {
      this.savingId = null;
    }
  }

  @action
  cancelBestForForm() {
    this.bestForFormMode = null;
    this.editingBestForId = null;
    this._teardownKeydownListener();
  }

  @action
  updateBestForField(field, event) {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (field === 'title' && this.bestForFormMode === 'new') {
      const slug = slugify(value);
      this.bestForForm = { ...this.bestForForm, title: value, slug };
    } else {
      this.bestForForm = { ...this.bestForForm, [field]: value };
    }
  }

  @action
  async saveBestFor() {
    this.savingId = 'save-best-for';
    this.message = null;
    const payload = { ...this.bestForForm };

    try {
      if (this.editingBestForId) {
        await ajax(`/admin/plugins/proxyviews-seo/best-for/${this.editingBestForId}`, {
          type: 'PUT',
          data: { best_for_use_case: payload },
        });
        this.message = { text: `Saved "${payload.title || payload.slug}".`, type: 'success' };
      } else {
        await ajax('/admin/plugins/proxyviews-seo/best-for', {
          type: 'POST',
          data: { best_for_use_case: payload },
        });
        this.message = { text: `Created use case.`, type: 'success' };
      }
      this.bestForFormMode = null;
      this.editingBestForId = null;
      this.clearMessageAfterDelay();
      await this.loadBestFor();
    } catch (err) {
      this.message = {
        text: err.responseJSON?.errors?.join(', ') || 'Failed to save use case.',
        type: 'danger',
      };
    } finally {
      this.savingId = null;
    }
  }

  @action
  async deleteBestFor(useCase) {
    this.dialog.yesNoConfirm({
      message: `Delete use case "${useCase.slug}"? This cannot be undone.`,
      didConfirm: async () => {
        this.savingId = 'delete-best-for-' + useCase.id;
        this.message = null;
        try {
          await ajax(`/admin/plugins/proxyviews-seo/best-for/${useCase.id}`, { type: 'DELETE' });
          this.bestForUseCases = this.bestForUseCases.filter((uc) => uc.id !== useCase.id);
          this.message = { text: `Deleted "${useCase.slug}".`, type: 'success' };
          this.clearMessageAfterDelay();
        } catch (err) {
          this.message = { text: 'Failed to delete use case.', type: 'danger' };
        } finally {
          this.savingId = null;
        }
      },
    });
  }

  // ── Best For Provider Management ─────────────────────────────────────

  @action
  async manageProviders(useCase) {
    this.managingBestForSlug = useCase.slug;
    this.managingBestForTitle = useCase.title;
    this.bestForProviders = [];
    this.bestForProviderSearch = '';
    this.bestForSearchResults = [];
    this.bestForDirtyOrder = false;
    await this.loadProviders(useCase.slug);
  }

  @action
  closeProviderManager() {
    if (this._providerSearchTimer) {
      clearTimeout(this._providerSearchTimer);
    }
    this.managingBestForSlug = null;
    this.managingBestForTitle = null;
    this.bestForProviders = [];
    this.bestForProviderSearch = '';
    this.bestForSearchResults = [];
    this.bestForDirtyOrder = false;
  }

  async loadProviders(slug) {
    try {
      const data = await ajax(`/admin/plugins/proxyviews-seo/best-for/${slug}/providers`);
      this.bestForProviders = data.providers || [];
    } catch (err) {
      this.message = { text: 'Failed to load providers.', type: 'danger' };
    }
  }

  get pinnedProviders() {
    return this.bestForProviders.filter((p) => p.status === 'pinned');
  }

  get autoProviders() {
    return this.bestForProviders.filter((p) => p.status === 'auto');
  }

  get excludedProviders() {
    return this.bestForProviders.filter((p) => p.status === 'excluded');
  }

  @action
  searchProvidersToAdd(e) {
    const query = e.target.value;
    this.bestForProviderSearch = query;

    // Debounce rapid keystrokes
    if (this._providerSearchTimer) {
      clearTimeout(this._providerSearchTimer);
    }
    if (query.length < 2) {
      this.bestForSearchResults = [];
      return;
    }
    this._providerSearchTimer = setTimeout(() => {
      this._runProviderSearch(query);
    }, 300);
  }

  async _runProviderSearch(query) {
    try {
      const data = await ajax(`/admin/plugins/proxyviews-seo/best-for/search-providers?q=${encodeURIComponent(query)}&use_case_slug=${this.managingBestForSlug}`);
      // Only apply if the query hasn't changed while fetching
      if (this.bestForProviderSearch === query) {
        this.bestForSearchResults = data.results || [];
      }
    } catch (err) {
      if (this.bestForProviderSearch === query) {
        this.bestForSearchResults = [];
      }
    }
  }

  @action
  async addProvider(providerId) {
    try {
      await ajax(`/admin/plugins/proxyviews-seo/best-for/${this.managingBestForSlug}/providers`, {
        type: 'POST',
        data: { provider_id: providerId, pin_to_top: true },
      });
      await this.loadProviders(this.managingBestForSlug);
      // Refresh search results so the just-added provider disappears from them
      if (this.bestForProviderSearch.length >= 2) {
        await this._runProviderSearch(this.bestForProviderSearch);
      }
      this.message = { text: 'Provider pinned.', type: 'success' };
      this.clearMessageAfterDelay();
    } catch (err) {
      this.message = { text: 'Failed to add provider.', type: 'danger' };
    }
  }

  @action
  async pinProvider(providerId) {
    try {
      await ajax(`/admin/plugins/proxyviews-seo/best-for/${this.managingBestForSlug}/providers/${providerId}`, {
        type: 'PUT',
        data: { pinned: true },
      });
      await this.loadProviders(this.managingBestForSlug);
    } catch (err) {
      this.message = { text: 'Failed to pin provider.', type: 'danger' };
    }
  }

  @action
  async unpinProvider(providerId) {
    try {
      await ajax(`/admin/plugins/proxyviews-seo/best-for/${this.managingBestForSlug}/providers/${providerId}`, {
        type: 'PUT',
        data: { pinned: false },
      });
      await this.loadProviders(this.managingBestForSlug);
    } catch (err) {
      this.message = { text: 'Failed to unpin provider.', type: 'danger' };
    }
  }

  @action
  async excludeProvider(providerId) {
    try {
      await ajax(`/admin/plugins/proxyviews-seo/best-for/${this.managingBestForSlug}/providers/${providerId}`, {
        type: 'PUT',
        data: { excluded: true, pinned: false },
      });
      await this.loadProviders(this.managingBestForSlug);
    } catch (err) {
      this.message = { text: 'Failed to exclude provider.', type: 'danger' };
    }
  }

  @action
  async restoreProvider(providerId) {
    try {
      await ajax(`/admin/plugins/proxyviews-seo/best-for/${this.managingBestForSlug}/providers/${providerId}`, {
        type: 'PUT',
        data: { excluded: false },
      });
      await this.loadProviders(this.managingBestForSlug);
    } catch (err) {
      this.message = { text: 'Failed to restore provider.', type: 'danger' };
    }
  }

  @action
  async removeProviderPlacement(providerId) {
    try {
      await ajax(`/admin/plugins/proxyviews-seo/best-for/${this.managingBestForSlug}/providers/${providerId}`, {
        type: 'DELETE',
      });
      await this.loadProviders(this.managingBestForSlug);
    } catch (err) {
      this.message = { text: 'Failed to remove provider.', type: 'danger' };
    }
  }

  @action
  moveProvider(providerId, direction) {
    const pinned = [...this.pinnedProviders];
    const idx = pinned.findIndex((p) => p.provider_id === providerId);
    if (idx === -1) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= pinned.length) return;

    const tmp = pinned[idx];
    pinned[idx] = pinned[swapIdx];
    pinned[swapIdx] = tmp;

    // Update sort_order on all pinned providers
    pinned.forEach((p, i) => {
      const fullProvider = this.bestForProviders.find((bp) => bp.provider_id === p.provider_id);
      if (fullProvider) fullProvider.placement = { ...fullProvider.placement, sort_order: i };
    });

    // Rebuild bestForProviders: pinned (new order) + auto + excluded
    this.bestForProviders = [
      ...pinned,
      ...this.autoProviders,
      ...this.excludedProviders,
    ];
    this.bestForDirtyOrder = true;
  }

  @action
  async saveOrder() {
    const orderedIds = this.pinnedProviders.map((p) => p.provider_id);
    try {
      await ajax(`/admin/plugins/proxyviews-seo/best-for/${this.managingBestForSlug}/reorder`, {
        type: 'PUT',
        data: { ordered_provider_ids: orderedIds },
      });
      this.bestForDirtyOrder = false;
      this.message = { text: 'Order saved.', type: 'success' };
      this.clearMessageAfterDelay();
    } catch (err) {
      this.message = { text: 'Failed to save order.', type: 'danger' };
    }
  }

  @action
  async saveCustomField(providerId, field, value) {
    try {
      await ajax(`/admin/plugins/proxyviews-seo/best-for/${this.managingBestForSlug}/providers/${providerId}`, {
        type: 'PUT',
        data: { [field]: value },
      });
      // Update local state
      const provider = this.bestForProviders.find((p) => p.provider_id === providerId);
      if (provider) {
        if (field === 'custom_description') provider.description = value;
        if (field === 'custom_proxy_type') provider.proxy_type = value;
      }
      this.message = { text: 'Saved.', type: 'success' };
      this.clearMessageAfterDelay();
    } catch (err) {
      this.message = { text: 'Failed to save.', type: 'danger' };
    }
  }

  @action
  updateCustomField(providerId, field, e) {
    const value = e.target.value;
    const provider = this.bestForProviders.find((p) => p.provider_id === providerId);
    if (provider) {
      if (field === 'custom_description') provider.description = value;
      if (field === 'custom_proxy_type') provider.proxy_type = value;
    }
    this.bestForProviders = [...this.bestForProviders];
  }

  async loadAlternatives() {
    try {
      const data = await ajax('/admin/plugins/proxyviews-seo/alternatives');
      this.alternatives = data.alternatives || [];
    } catch (err) {
      this.message = { text: 'Failed to load alternatives.', type: 'danger' };
    }
  }

  @action
  startNewAlternative() {
    this.alternativeForm = { ...BLANK_ALTERNATIVE };
    this.editingAlternativeId = null;
    this.alternativeFormMode = 'new';
    this._setupKeydownListener();
  }

  @action
  async startEditAlternative(alternative) {
    this.savingId = 'load-alternative-' + alternative.id;
    try {
      const data = await ajax(`/admin/plugins/proxyviews-seo/alternatives/${alternative.id}`);
      const alt = data.alternative;
      this.alternativeForm = {
        provider_slug: alt.provider_slug,
        meta_title: alt.meta_title || '',
        meta_description: alt.meta_description || '',
        custom_alternatives_slugs_text: (alt.custom_alternatives_slugs || []).join(', '),
      };
      this.editingAlternativeId = alternative.id;
      this.alternativeFormMode = 'edit';
      this._setupKeydownListener();
    } catch (err) {
      this.message = { text: 'Failed to load alternative.', type: 'danger' };
    } finally {
      this.savingId = null;
    }
  }

  @action
  cancelAlternativeForm() {
    this.alternativeFormMode = null;
    this.editingAlternativeId = null;
    this._teardownKeydownListener();
  }

  @action
  updateAlternativeField(field, event) {
    this.alternativeForm = { ...this.alternativeForm, [field]: event.target.value };
  }

  @action
  async saveAlternative() {
    this.savingId = 'save-alternative';
    this.message = null;

    const payload = {
      provider_slug: this.alternativeForm.provider_slug,
      meta_title: this.alternativeForm.meta_title,
      meta_description: this.alternativeForm.meta_description,
      custom_alternatives_slugs: this.alternativeForm.custom_alternatives_slugs_text
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    };

    try {
      if (this.editingAlternativeId) {
        await ajax(`/admin/plugins/proxyviews-seo/alternatives/${this.editingAlternativeId}`, {
          type: 'PUT',
          data: { alternative_metadata: payload },
        });
        this.message = {
          text: `Saved alternatives for "${payload.provider_slug}".`,
          type: 'success',
        };
      } else {
        await ajax('/admin/plugins/proxyviews-seo/alternatives', {
          type: 'POST',
          data: { alternative_metadata: payload },
        });
        this.message = { text: `Created alternatives override.`, type: 'success' };
      }
      this.alternativeFormMode = null;
      this.editingAlternativeId = null;
      this.clearMessageAfterDelay();
      await this.loadAlternatives();
    } catch (err) {
      this.message = {
        text: err.responseJSON?.errors?.join(', ') || 'Failed to save alternative.',
        type: 'danger',
      };
    } finally {
      this.savingId = null;
    }
  }

  @action
  async deleteAlternative(alternative) {
    this.dialog.yesNoConfirm({
      message: `Delete alternative override for "${alternative.provider_slug}"? This cannot be undone.`,
      didConfirm: async () => {
        this.savingId = 'delete-alt-' + alternative.id;
        this.message = null;
        try {
          await ajax(`/admin/plugins/proxyviews-seo/alternatives/${alternative.id}`, {
            type: 'DELETE',
          });
          this.alternatives = this.alternatives.filter((a) => a.id !== alternative.id);
          this.message = {
            text: `Deleted override for "${alternative.provider_slug}".`,
            type: 'success',
          };
          this.clearMessageAfterDelay();
        } catch (err) {
          this.message = { text: 'Failed to delete alternative.', type: 'danger' };
        } finally {
          this.savingId = null;
        }
      },
    });
  }

  @action
  toggleRobotsEnabled(event) {
    this.robotsEnabled = event.target.checked;
  }

  @action
  updateRobotsCustomRules(event) {
    this.robotsCustomRules = event.target.value;
  }

  @action
  updateDraftRobotsRules(event) {
    this.draftRobotsRules = event.target.value;
  }

  @action
  async previewDraft() {
    this.draftLoading = true;
    this.message = null;
    try {
      const data = await ajax('/admin/plugins/proxyviews-seo/robots/preview-draft', {
        type: 'POST',
        data: { custom_rules: this.draftRobotsRules },
      });
      this.draftPreview = data.preview || '';
    } catch (err) {
      this.message = {
        text: err.responseJSON?.error || 'Failed to preview draft robots.txt.',
        type: 'danger',
      };
    } finally {
      this.draftLoading = false;
    }
  }

  @action
  async saveRobots() {
    this.robotsSaving = true;
    this.message = null;
    try {
      const data = await ajax('/admin/plugins/proxyviews-seo/robots', {
        type: 'PUT',
        data: {
          enabled: this.robotsEnabled,
          custom_rules: this.robotsCustomRules,
        },
      });
      this.robotsEnabled = !!data.robots.enabled;
      this.robotsCustomRules = data.robots.custom_rules || '';
      this.robotsPreview = data.robots.preview || '';
      this.robotsUpdatedAt = data.robots.updated_at || null;
      this.robotsUpdatedBy = data.robots.updated_by || null;
      this.message = { text: 'robots.txt settings saved.', type: 'success' };
      this.clearMessageAfterDelay();
    } catch (err) {
      this.message = { text: 'Failed to save robots.txt settings.', type: 'danger' };
    } finally {
      this.robotsSaving = false;
    }
  }

  @action
  copySitemapUrl() {
    if (this.sitemapUrl) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(this.sitemapUrl);
      }
      this.message = { text: 'Sitemap URL copied to clipboard!', type: 'success' };
      this.clearMessageAfterDelay();
    }
  }

  @action
  async regenerateSitemap() {
    this.sitemapRegenerating = true;
    this.message = null;
    try {
      const data = await ajax('/admin/plugins/proxyviews-seo/sitemap/regenerate', {
        type: 'POST',
      });
      this.sitemapEnabled = !!data.sitemap.enabled;
      this.sitemapUrl = data.sitemap.sitemap_url || '';
      this.sitemapLastGeneratedAt = data.sitemap.last_generated_at || null;
      this.sitemapUrlCount = data.sitemap.url_count || null;
      this.message = {
        text: `Sitemap regenerated with ${this.sitemapUrlCount} URLs.`,
        type: 'success',
      };
      this.clearMessageAfterDelay();
    } catch (err) {
      this.message = { text: 'Failed to regenerate sitemap.', type: 'danger' };
    } finally {
      this.sitemapRegenerating = false;
    }
  }

  scrollToTable() {
    const el = document.querySelector('.pd-admin-table-container');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
}
